import React, { Fragment } from "react";

import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Check, Clear as Cross } from "@material-ui/icons";
import { Controller } from "react-hook-form/dist/react-hook-form.ie11";
import { green } from "@material-ui/core/colors";
import clsx from "clsx";

import {
  FormHeader,
  MaskedTextField,
  Page,
  VisualValidator,
} from "@hubcapp/components";
import { SetPasswordProps } from "./types";
import {
  Eye as VisibilityIcon,
  EyeOff as VisibilityOffIcon,
} from "react-feather";
import { get } from "lodash";
import Button from "@ds-developer/button";

const useStyles = makeStyles({
  resendContainer: {
    display: "flex",
    alignItems: "center",
  },
  passwordContainer: {
    marginTop: "48px",
  },
  passwordTextContainer: {
    padding: "12px",
  },
  resendIcon: {
    fontSize: 18,
  },
  resendSuccessIcon: {
    color: green[500],
  },
});

const SetPassword: React.FC<SetPasswordProps> = ({
  control,
  errors,
  isLoading = false,
  isValid,
  onResendClicked,
  onSubmit,
  primaryErrorMessage,
  resendLoading = false,
  resendStatus,
  handleDigitCodeEvent,
  showPassword,
  handleClickShowPassword,
  hasCapital = false,
  hasNumber = false,
  isLongerThan8Chars = false,
  showConfirmPassword,
  handleClickShowConfirmPassword,
  register,
  handleSetPasswordEvent,
  handleEnterPasswordEvent,
  handleConfirmPasswordEvent,
  handleConfirmPasswordErrorEvent,
  handleEnterPasswordErrorEvent,
  handleDigitCodeErrorEvent,
  showResendButton = false,
}) => {
  const classes = useStyles();

  return (
    <Page title="Enter access code">
      <form onSubmit={onSubmit}>
        <FormHeader
          title="Enter access code"
          subtitle={
            Boolean(primaryErrorMessage) ? (
              primaryErrorMessage
            ) : (
              <Fragment>
                <span>
                  We have sent an access code to the mobile number on record.
                </span>
                <br />
                <br />
                <span>Check your device and enter the 6-digit code here.</span>
              </Fragment>
            )
          }
          error={Boolean(primaryErrorMessage)}
        />
        {/* This needs to use the Controller syntax due to the text masking */}
        <Controller
          as={MaskedTextField}
          autoComplete="one-time-code"
          control={control}
          error={Boolean(errors?.accessCode)}
          helperText={errors?.accessCode?.message}
          helperTextProps={{
            ["data-test"]: "access-code-helper-text",
          }}
          inputProps={{
            ["data-test"]: "access-code-input",
          }}
          mask={[
            /[0-9]/,
            /[0-9]/,
            /[0-9]/,
            " ",
            "-",
            " ",
            /[0-9]/,
            /[0-9]/,
            /[0-9]/,
          ]}
          name="accessCode"
          placeholder="6-digit code"
          onClick={() => handleDigitCodeEvent()}
          onBlur={() => {
            if (errors?.accessCode) {
              handleDigitCodeErrorEvent();
            }
          }}
        />

        <div className={classes.resendContainer}>
          <Typography color="textSecondary" variant="body1">
            Didn’t receive the code?
          </Typography>
          <Button
            color="primary"
            size="large"
            disabled={
              resendLoading || resendStatus === "SUCCESS" || !showResendButton
            }
            data-test="resend-button"
            onClick={() => onResendClicked()}
          >
            Resend it
          </Button>
          {resendLoading && (
            <CircularProgress size={15} className={classes.resendIcon} />
          )}
          {!resendLoading && resendStatus === "SUCCESS" && (
            <Check
              className={clsx(classes.resendIcon, classes.resendSuccessIcon)}
            />
          )}
          {!resendLoading && resendStatus === "FAILURE" && (
            <Cross color="error" className={classes.resendIcon} />
          )}
        </div>

        <div className={classes.passwordContainer}>
          <FormHeader
            title="Now enter a new password"
            subtitle="Hint: Get your browser to suggest a strong password or save it."
          />
          <div className={get(classes, "fields")}>
            <TextField
              autoComplete="new-password"
              error={Boolean(errors?.password)}
              fullWidth
              helperText={errors?.password?.message}
              inputRef={register}
              label="Enter your new password"
              name="password"
              type={showPassword ? "text" : "password"}
              variant="outlined"
              onClick={handleEnterPasswordEvent}
              onBlur={() => {
                if (errors?.password) {
                  handleEnterPasswordErrorEvent();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                    >
                      {showPassword ? (
                        <VisibilityIcon />
                      ) : (
                        <VisibilityOffIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              FormHelperTextProps={{
                // @ts-ignore
                ["data-test"]: "password-helper-text",
              }}
              inputProps={{
                // @ts-ignore
                ["data-test"]: "password-field",
              }}
            />
            <Grid
              container
              justify="center"
              className={classes.passwordTextContainer}
            >
              <Typography variant="body1" color="textSecondary">
                Your password must contain:
              </Typography>
            </Grid>

            <div>
              <Grid container direction="row" justify="center">
                <VisualValidator
                  description="Capital"
                  mainText="C"
                  isValid={hasCapital}
                  data-test="validator-capital"
                />
                <VisualValidator
                  description="Number"
                  mainText="1"
                  isValid={hasNumber}
                  data-test="validator-number"
                />
                <VisualValidator
                  description="8 characters"
                  mainText=">8"
                  isValid={isLongerThan8Chars}
                  data-test="validator-length"
                />
              </Grid>
            </div>

            <TextField
              autoComplete="new-password"
              error={Boolean(errors?.confirmPassword)}
              fullWidth
              helperText={errors?.confirmPassword?.message}
              inputRef={register}
              label="Re-enter your new password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              variant="outlined"
              onClick={handleConfirmPasswordEvent}
              onBlur={() => {
                if (errors?.confirmPassword) {
                  handleConfirmPasswordErrorEvent();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowConfirmPassword}
                    >
                      {showConfirmPassword ? (
                        <VisibilityIcon />
                      ) : (
                        <VisibilityOffIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              FormHelperTextProps={{
                // @ts-ignore
                ["data-test"]: "confirm-password-helper-text",
              }}
              inputProps={{
                // @ts-ignore
                ["data-test"]: "confirm-password-field",
              }}
            />
          </div>
        </div>
        <Box marginTop={3} marginBottom={2}>
          <Button
            type="submit"
            color="primary"
            size="large"
            variant="contained"
            disabled={!isValid}
            loading={isLoading}
            data-test="submit-button"
            onClick={handleSetPasswordEvent}
          >
            Set new password
          </Button>
        </Box>
      </form>
    </Page>
  );
};

export default SetPassword;
